import { sourceColor, sourceFullName } from '#app/utils/search-lib'
import { type SnabbHit, type SnabbResults } from '#app/utils/snabbsok'
import {
	disambiguateSameSource,
	displayTitle,
} from '#app/utils/snabbsok-labels'

/**
 * Navbarens sökdialog visar motorns två körfält (interna och externa träffar)
 * som EN lista, i samma ordning som ⌘K-modalen på internetmedicin.se: tre
 * interna, två externa, sedan ungefär två interna per extern. Körfälten har
 * ingen gemensam relevanspoäng, så varvningen är en presentationsregel - den
 * enda logiken här som speglar sajten (`searchSnabbsok` i sajtens
 * pm-search.server.ts). Titlar, färger och källnamn kommer från samma
 * hjälpare som Snabbsök-kortet och sök-API:t använder.
 */

const MAX_ROWS = 30
const MAX_EXTERNAL = 5
const INTERNAL_FIRST = 3
const EXTERNAL_FIRST = 2

export type DialogRow = {
	/** Stabil nyckel inom en svarslista. */
	key: string
	hit: SnabbHit
	isExternal: boolean
	title: string
	href: string
	/** Källnamnet som loggas och skickas till Plausible ('Internetmedicin' för interna). */
	source: string
	chipLabel: string
	chipColor: string
	isSpeciality: boolean
	/** 1-baserad plats i motorns körfält - samma numrering som impressionsradens
	 *  `results_shown`, så klickraden går att para ihop med den. */
	lanePosition: number
	sitelinks: { title: string; url: string }[]
}

/** Bara http(s) når DOM:en som href; React stoppar inte javascript:-länkar. */
function isHttpUrl(url: string | undefined): url is string {
	return !!url && /^https?:\/\//i.test(url)
}

function laneRows(hits: SnabbHit[], isIM: boolean, limit: number): DialogRow[] {
	const labels = disambiguateSameSource(hits, isIM)
	const rows: DialogRow[] = []
	const seen = new Set<string>()
	hits.forEach((hit, index) => {
		if (rows.length >= limit || !isHttpUrl(hit.url)) return
		// Samma artikel kan komma tillbaka två gånger (olika skiftläge i
		// sökvägen, eller samma sida under två avsnitt); första vinner.
		const key = `${isIM ? 'im' : 'ext'}:${(hit.page_url ?? hit.url).toLowerCase()}`
		if (seen.has(key)) return
		seen.add(key)
		const source = isIM ? 'Internetmedicin' : hit.source
		const sitelinkUrls = new Set<string>()
		rows.push({
			key,
			hit,
			isExternal: !isIM,
			title: labels.get(hit) ?? displayTitle(hit, isIM),
			href: hit.url,
			source,
			chipLabel: sourceFullName(source),
			chipColor: sourceColor(source),
			isSpeciality: isIM && hit.type === 'SPECIALITY',
			lanePosition: index + 1,
			sitelinks: (hit.sitelinks ?? []).filter(s => {
				if (!isHttpUrl(s.url) || sitelinkUrls.has(s.url)) return false
				sitelinkUrls.add(s.url)
				return true
			}),
		})
	})
	return rows
}

export function dialogRows(results: SnabbResults): DialogRow[] {
	const internal = laneRows(results.im, true, MAX_ROWS)
	const external = laneRows(results.ext, false, MAX_EXTERNAL)

	const rows = [
		...internal.slice(0, INTERNAL_FIRST),
		...external.slice(0, EXTERNAL_FIRST),
	]
	const internalRest = internal.slice(INTERNAL_FIRST)
	const externalRest = external.slice(EXTERNAL_FIRST)
	let i = 0
	let e = 0
	while (
		rows.length < MAX_ROWS &&
		(i < internalRest.length || e < externalRest.length)
	) {
		for (let n = 0; n < 2 && i < internalRest.length; n++) {
			if (rows.length < MAX_ROWS) rows.push(internalRest[i++]!)
		}
		if (e < externalRest.length && rows.length < MAX_ROWS) {
			rows.push(externalRest[e++]!)
		}
	}
	return rows
}
